/**
 * AdminPage — Administration surface host.
 *
 * The in-page AdminMenu sidebar was removed (brief-13-shell-nav-rail
 * follow-up, 2026-07-25): NavRail's expandable Administration entry now
 * covers the same navigation, so this page renders full-width content only.
 * AdminMenu.tsx itself is kept — NavRail imports its MENU_ITEMS /
 * isMenuItemActive() as the shared source of truth.
 *
 * Routes:
 *  /admin              → placeholder (Tenants active, coming cycle 3+)
 *  /admin/sub-clients  → SubClientTile grid with 4 mock entries per brief §2.3
 *  /admin/audit-log    → Audit log table with mock entries (DOPLA-598)
 *
 * Role gate (vertical-ref mode): renders a 403-style notice for users without
 * the broker-admin role (decision-2026-08-03-role-model-planes; the gate itself
 * is ADMIN_SURFACE_ROLES in auth/surfaceAccess).
 *
 * Refs: marketplace-broker-c2 brief §3 stage 4, §2.3 (mock data)
 */

import React, { useState, useMemo, Suspense, lazy } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { canUseAdminSurface } from '../auth/surfaceAccess';
import SubClientTile, { type SubClientData } from '../components/SubClientTile';
import SubClientDetailPage from './SubClientDetailPage';
import RouteNotFound from '../components/RouteNotFound';
import { hasOperatorProvisioning } from '../shell/routes';
import { isAuthenticatedBroker } from '../config/schema';
import type { Route } from '../shell/Router';

/**
 * The operator provisioning surface is CODE-SPLIT and reached only through the
 * meta-mode branch below (decision-2026-07-25-broker-mode-split, restated gate 9c).
 * A tenant-mode session never takes that branch, so it never fetches this chunk.
 *
 * Note what this does and does not claim: the chunk is present in the shared
 * artifact — one artifact is what option C chose. What is proven is that the tenant
 * mode registers no route to it, renders no entry for it, and never loads it.
 * Bundle-absence is option A (per-deployment builds), deliberately not taken.
 */
const SandboxProvisioningPage = lazy(() => import('./SandboxProvisioningPage'));
import { useSubscriptions, type SubscriptionRow } from '../hooks/useSubscriptions';
import { useOrders, type OrderRow, type OrderDetail } from '../hooks/useOrders';

// Mock sub-client data per brief §2.3
const MOCK_SUB_CLIENTS: SubClientData[] = [
  {
    slug: 'acme-capital',
    name: 'Acme Capital',
    context: 'European mid-market PE fund. Securitization pilot, Deal vertical.',
    lifecycle: 'provisioned',
  },
  {
    slug: 'nordic-fund',
    name: 'Nordic Fund Partners',
    context: 'Nordic infrastructure debt. Active on all Deal modules.',
    lifecycle: 'provisioned',
  },
  {
    slug: 'alpine-investments',
    name: 'Alpine Investments',
    context: 'Swiss family office. Onboarding in progress.',
    lifecycle: 'pending',
  },
  {
    slug: 'legacy-trust',
    name: 'Legacy Trust AG',
    context: 'Account suspended — pending compliance review.',
    lifecycle: 'suspended',
  },
];

interface AdminPageProps {
  activePath: Route;
  onNavigate: (path: Route) => void;
}

// ── Role gate ─────────────────────────────────────────────────────────────────

function AdminAccessDenied() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-12)',
      }}
    >
      <p className="dp-eyebrow">Access denied</p>
      <h2>Administration requires elevated access</h2>
      <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
        Your account needs the <code>broker-admin</code> role to access this
        surface.
      </p>
    </div>
  );
}

// ── Sub-clients view ──────────────────────────────────────────────────────────

function SubClientsView({ onNavigate }: { onNavigate: (path: Route) => void }) {
  return (
    <div className="dp-shell__content">
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <p className="dp-eyebrow">Sub-clients</p>
        <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Managed tenants</h2>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Sub-client management is in early preview. Full lifecycle controls ship in cycle 3+.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--sp-5)',
        }}
      >
        {MOCK_SUB_CLIENTS.map((sc) => (
          <SubClientTile
            key={sc.slug}
            data={sc}
            onManage={(slug) => {
              onNavigate(`/admin/sub-clients/${slug}` as Route);
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Audit log view (DOPLA-598) ────────────────────────────────────────────

type AuditAction =
  | 'user.login'
  | 'user.logout'
  | 'subclient.create'
  | 'subclient.update'
  | 'subclient.suspend'
  | 'config.update'
  | 'role.assign'
  | 'role.revoke'
  | 'subscription.create'
  | 'subscription.cancel';

interface AuditLogEntry {
  id: string;
  timestamp: string;
  actor: string;
  action: AuditAction;
  target: string;
  detail: string;
}

// Mock audit log entries — will be replaced by a real API in a later cycle
const MOCK_AUDIT_LOG: AuditLogEntry[] = [
  {
    id: 'aud-001',
    timestamp: '2026-05-23T09:14:22Z',
    actor: 'alice.mueller@acme-capital.com',
    action: 'user.login',
    target: 'acme-capital',
    detail: 'Successful login via SSO (PKCE)',
  },
  {
    id: 'aud-002',
    timestamp: '2026-05-23T08:47:05Z',
    actor: 'ops@doplace.io',
    action: 'subclient.create',
    target: 'alpine-investments',
    detail: 'Sub-client "Alpine Investments" provisioned',
  },
  {
    id: 'aud-003',
    timestamp: '2026-05-22T17:30:11Z',
    actor: 'ops@doplace.io',
    action: 'role.assign',
    target: 'alice.mueller@acme-capital.com',
    detail: 'Assigned role vertical-product-owner',
  },
  {
    id: 'aud-004',
    timestamp: '2026-05-22T16:05:33Z',
    actor: 'ops@doplace.io',
    action: 'subclient.suspend',
    target: 'legacy-trust',
    detail: 'Sub-client "Legacy Trust AG" suspended — compliance review',
  },
  {
    id: 'aud-005',
    timestamp: '2026-05-22T14:22:09Z',
    actor: 'bob.norris@nordic-fund.com',
    action: 'subscription.create',
    target: 'nordic-fund',
    detail: 'Subscribed to "Deal Flow Analytics" module',
  },
  {
    id: 'aud-006',
    timestamp: '2026-05-22T11:58:44Z',
    actor: 'ops@doplace.io',
    action: 'config.update',
    target: 'broker-spa',
    detail: 'Updated runtime config: auth.sessionTimeout → 3600',
  },
  {
    id: 'aud-007',
    timestamp: '2026-05-21T09:03:17Z',
    actor: 'alice.mueller@acme-capital.com',
    action: 'subscription.cancel',
    target: 'acme-capital',
    detail: 'Cancelled subscription to "Risk Dashboard"',
  },
  {
    id: 'aud-008',
    timestamp: '2026-05-21T08:15:00Z',
    actor: 'ops@doplace.io',
    action: 'role.revoke',
    target: 'jane.doe@legacy-trust.ch',
    detail: 'Revoked role core-developer',
  },
  {
    id: 'aud-009',
    timestamp: '2026-05-20T18:44:29Z',
    actor: 'bob.norris@nordic-fund.com',
    action: 'user.logout',
    target: 'nordic-fund',
    detail: 'User logged out',
  },
  {
    id: 'aud-010',
    timestamp: '2026-05-20T14:10:55Z',
    actor: 'ops@doplace.io',
    action: 'subclient.update',
    target: 'acme-capital',
    detail: 'Updated sub-client context description',
  },
];

const ACTION_BADGE: Record<string, { bg: string; fg: string }> = {
  'user.login':           { bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  'user.logout':          { bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)'  },
  'subclient.create':     { bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  'subclient.update':     { bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)'   },
  'subclient.suspend':    { bg: 'var(--dp-crimson-100)', fg: 'var(--dp-crimson-600)' },
  'config.update':        { bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)'   },
  'role.assign':          { bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  'role.revoke':          { bg: 'var(--dp-crimson-100)', fg: 'var(--dp-crimson-600)' },
  'subscription.create':  { bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
  'subscription.cancel':  { bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)'  },
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
}

function AuditLogView() {
  const [filterAction, setFilterAction] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const actionTypes = useMemo(() => {
    const unique = Array.from(new Set(MOCK_AUDIT_LOG.map((e) => e.action)));
    unique.sort();
    return unique;
  }, []);

  const filtered = useMemo(() => {
    return MOCK_AUDIT_LOG.filter((entry) => {
      if (filterAction !== 'all' && entry.action !== filterAction) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          entry.actor.toLowerCase().includes(q) ||
          entry.target.toLowerCase().includes(q) ||
          entry.detail.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [filterAction, searchQuery]);

  return (
    <div className="dp-shell__content">
      {/* Header */}
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <p className="dp-eyebrow">Audit log</p>
        <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Activity history</h2>
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          View administrative actions across all tenants. Production data will be sourced from the platform audit API.
        </p>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-5)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          placeholder="Search actor, target, or detail\u2026"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 240px',
            maxWidth: 360,
            padding: 'var(--sp-2) var(--sp-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface)',
            color: 'var(--fg-1)',
            outline: 'none',
          }}
        />
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          style={{
            padding: 'var(--sp-2) var(--sp-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface)',
            color: 'var(--fg-1)',
            cursor: 'pointer',
          }}
        >
          <option value="all">All actions</option>
          {actionTypes.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-meta)',
            color: 'var(--fg-4)',
          }}
        >
          {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--bg-surface)',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--border-default)',
                background: 'var(--bg-recessed)',
              }}
            >
              {['Timestamp', 'Actor', 'Action', 'Target', 'Detail'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: 'var(--sp-3) var(--sp-4)',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 'var(--fs-meta)',
                    color: 'var(--fg-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 'var(--sp-8)',
                    textAlign: 'center',
                    color: 'var(--fg-4)',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  No audit log entries match the current filters.
                </td>
              </tr>
            ) : (
              filtered.map((entry) => {
                const badge = ACTION_BADGE[entry.action] ?? { bg: 'var(--dp-stone-200)', fg: 'var(--dp-stone-700)' };
                return (
                  <tr
                    key={entry.id}
                    style={{ borderBottom: '1px solid var(--border-subtle)' }}
                  >
                    <td
                      style={{
                        padding: 'var(--sp-3) var(--sp-4)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--fs-meta)',
                        color: 'var(--fg-2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td
                      style={{
                        padding: 'var(--sp-3) var(--sp-4)',
                        color: 'var(--fg-1)',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.actor}
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--fs-meta)',
                          fontWeight: 600,
                          background: badge.bg,
                          color: badge.fg,
                          borderRadius: 'var(--radius-sm)',
                          padding: '2px 7px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: 'var(--sp-3) var(--sp-4)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--fs-meta)',
                        color: 'var(--fg-2)',
                      }}
                    >
                      {entry.target}
                    </td>
                    <td
                      style={{
                        padding: 'var(--sp-3) var(--sp-4)',
                        color: 'var(--fg-3)',
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.detail}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Subscription approval queue (WP-SL-V4) ──────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function PriceCell({ snap }: { snap: Record<string, unknown> | null }) {
  if (!snap) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  const cents    = snap.price_cents as number ?? 0;
  const currency = (snap.currency as string) ?? 'CHF';
  const period   = snap.billing_period as string | null;
  const trial    = snap.trial_days as number ?? 0;
  if (cents === 0 && trial > 0) {
    return (
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', fontWeight: 600,
        background: 'var(--dp-teal-100)', color: 'var(--dp-teal-700)',
        borderRadius: 'var(--radius-sm)', padding: '2px 7px',
      }}>
        TRIAL · {trial}d
      </span>
    );
  }
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)' }}>
      {new Intl.NumberFormat('en-CH', { style: 'currency', currency }).format(cents / 100)}
      {period ? <span style={{ color: 'var(--fg-4)' }}>/{period}</span> : null}
    </span>
  );
}

function SubscriptionQueueView({ token }: { token: string | null }) {
  const { state, reload } = useSubscriptions(token, { approval_state: 'requested' });
  const [actionState, setActionState] = React.useState<Record<string, 'loading'|'done'|'error'>>({});

  async function handleApprove(id: string) {
    if (!token) return;
    setActionState((s) => ({ ...s, [id]: 'loading' }));
    try {
      const res = await fetch(`/api/broker/v1/subscriptions/${id}/approve`, {
        cache: 'no-store',
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionState((s) => ({ ...s, [id]: res.ok ? 'done' : 'error' }));
      if (res.ok) setTimeout(reload, 600);
    } catch {
      setActionState((s) => ({ ...s, [id]: 'error' }));
    }
  }

  async function handleReject(id: string, reason?: string) {
    if (!token) return;
    setActionState((s) => ({ ...s, [id]: 'loading' }));
    try {
      const res = await fetch(`/api/broker/v1/subscriptions/${id}/reject`, {
        cache: 'no-store',
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason ?? 'Rejected by operator' }),
      });
      setActionState((s) => ({ ...s, [id]: res.ok ? 'done' : 'error' }));
      if (res.ok) setTimeout(reload, 600);
    } catch {
      setActionState((s) => ({ ...s, [id]: 'error' }));
    }
  }

  const pending: SubscriptionRow[] = state.tag === 'loaded' ? state.subscriptions : [];

  return (
    <div className="dp-shell__content">
      <div style={{ marginBottom: 'var(--sp-6)', display: 'flex', alignItems: 'baseline', gap: 'var(--sp-4)' }}>
        <div>
          <p className="dp-eyebrow">Approval queue</p>
          <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Pending subscriptions</h2>
          <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            Paid-plan subscription requests awaiting vertical operator approval.
            Trial plans auto-approve; they will not appear here.
          </p>
        </div>
        <button
          onClick={reload}
          style={{
            marginLeft: 'auto', padding: 'var(--sp-2) var(--sp-4)',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
            background: 'var(--bg-recessed)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--fg-2)',
            flexShrink: 0,
          }}
        >
          Refresh
        </button>
      </div>

      {state.tag === 'loading' && (
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Loading…
        </p>
      )}
      {state.tag === 'error' && (
        <p style={{ color: 'var(--dp-crimson-600)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Failed to load subscriptions ({state.errorCode}). Check your role or API status.
        </p>
      )}

      {state.tag === 'loaded' && pending.length === 0 && (
        <div style={{
          padding: 'var(--sp-12)', textAlign: 'center',
          color: 'var(--fg-3)', fontFamily: 'var(--font-sans)',
          border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)',
        }}>
          No pending subscription requests.
        </div>
      )}

      {pending.length > 0 && (
        <div style={{
          overflowX: 'auto', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--bg-recessed)' }}>
                {['Tenant', 'Service', 'Plan', 'Price', 'Requested', 'Actions'].map((h) => (
                  <th key={h} style={{
                    padding: 'var(--sp-3) var(--sp-4)', textAlign: 'left', fontWeight: 600,
                    fontSize: 'var(--fs-meta)', color: 'var(--fg-3)',
                    textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map((sub) => {
                const act = actionState[sub.id];
                return (
                  <tr key={sub.id} style={{
                    borderBottom: '1px solid var(--border-subtle)',
                    opacity: act === 'done' ? 0.4 : 1,
                    transition: 'opacity 0.3s',
                  }}>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)', fontWeight: 500 }}>
                      {sub.tenant_name ?? <span style={{ color: 'var(--fg-4)' }}>{sub.tenant_id?.slice(0,8) ?? '—'}</span>}
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                      {sub.service_name ?? sub.service_id}
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)' }}>
                      {sub.plan_name ?? sub.plan_id}
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                      <PriceCell snap={sub.terms_snapshot} />
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)', color: 'var(--fg-3)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', whiteSpace: 'nowrap' }}>
                      {formatDate(sub.created_at)}
                    </td>
                    <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                      {act === 'loading' ? (
                        <span style={{ color: 'var(--fg-4)', fontSize: 'var(--fs-meta)' }}>…</span>
                      ) : act === 'done' ? (
                        <span style={{ color: 'var(--dp-forest-700)', fontSize: 'var(--fs-meta)' }}>✓ Done</span>
                      ) : act === 'error' ? (
                        <span style={{ color: 'var(--dp-crimson-600)', fontSize: 'var(--fs-meta)' }}>Error</span>
                      ) : (
                        <span style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                          <button
                            onClick={() => handleApprove(sub.id)}
                            className="dp-btn dp-btn--sm dp-btn--primary"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(sub.id)}
                            className="dp-btn dp-btn--sm dp-btn--ghost"
                            style={{ color: 'var(--dp-crimson-600)' }}
                          >
                            Reject
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Order history view (WP-SL-V5) ────────────────────────────────────────────

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-CH', { style: 'currency', currency }).format(cents / 100);
}

function RailBadge({ rail }: { rail: string }) {
  const isB2B = rail === 'b2b_invoice';
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', fontWeight: 600,
      background: isB2B ? 'var(--dp-warn-100)' : 'var(--dp-teal-100)',
      color: isB2B ? 'var(--dp-warn-700)' : 'var(--dp-teal-700)',
      borderRadius: 'var(--radius-sm)', padding: '2px 7px',
    }}>
      {isB2B ? 'B2B Invoice' : 'B2C Card'}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string }> = {
    pending_payment: { bg: 'var(--dp-warn-100)',   fg: 'var(--dp-warn-700)' },
    settled:         { bg: 'var(--dp-forest-100)', fg: 'var(--dp-forest-700)' },
    cancelled:       { bg: 'var(--dp-stone-200)',  fg: 'var(--dp-stone-700)' },
  };
  const s = styles[status] ?? styles.cancelled;
  return (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', fontWeight: 600,
      background: s.bg, color: s.fg, borderRadius: 'var(--radius-sm)', padding: '2px 7px',
    }}>
      {status.replace('_', ' ')}
    </span>
  );
}

function OrderDetailModal({
  order, token, onClose, onSettled,
}: {
  order: OrderRow;
  token: string | null;
  onClose: () => void;
  onSettled: () => void;
}) {
  const [detail, setDetail] = React.useState<OrderDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [settling, setSettling] = React.useState(false);
  const [settleRef, setSettleRef] = React.useState('');
  const [settleError, setSettleError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    fetch(`/api/broker/v1/orders/${order.id}`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { setDetail(d as OrderDetail); setLoading(false); })
      .catch(() => setLoading(false));
  }, [order.id, token]);

  async function handleSettle() {
    if (!token) return;
    setSettling(true);
    setSettleError(null);
    try {
      const res = await fetch(`/api/broker/v1/orders/${order.id}/settle`, {
        cache: 'no-store',
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settlement_ref: settleRef || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        setSettleError(err.error ?? `HTTP ${res.status}`);
        setSettling(false);
        return;
      }
      setSettling(false);
      onSettled();
      onClose();
    } catch {
      setSettleError('Network error');
      setSettling(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'var(--scrim)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--sp-6)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-4)', width: '100%', maxWidth: 640,
        maxHeight: '85vh', overflow: 'auto', padding: 'var(--sp-8)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--sp-6)' }}>
          <div>
            <p className="dp-eyebrow">Order detail</p>
            <h3 style={{ margin: 'var(--sp-1) 0' }}>{order.tenant_name ?? 'Unknown tenant'}</h3>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-4)' }}>
              {order.id}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--fg-3)' }}
          >✕</button>
        </div>

        {/* Summary row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 'var(--sp-3)',
          background: 'var(--bg-recessed)', borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-4)', marginBottom: 'var(--sp-6)',
          fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
        }}>
          <div><span style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>Total</span><br />
            <strong style={{ fontFamily: 'var(--font-mono)' }}>
              {fmtCurrency(order.total_cents, order.currency)}
            </strong>
          </div>
          <div><span style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>Rail</span><br />
            <RailBadge rail={order.rail} />
          </div>
          <div><span style={{ color: 'var(--fg-3)', fontSize: 'var(--fs-meta)' }}>Status</span><br />
            <StatusBadge status={order.status} />
          </div>
        </div>

        {/* Line items */}
        {loading ? (
          <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>Loading items…</p>
        ) : detail?.items?.length ? (
          <div style={{
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
            overflow: 'hidden', marginBottom: 'var(--sp-6)',
          }}>
            {detail.items.map((item, idx) => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderBottom: idx < detail.items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{item.service_name ?? item.service_id}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                    {item.plan_name ?? item.plan_id}
                  </div>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                  {item.approval_state
                    ? <StatusBadge status={item.approval_state} />
                    : null}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)', textAlign: 'right' }}>
                  {item.price_cents === 0
                    ? <span style={{ color: 'var(--dp-teal-700)', fontWeight: 600 }}>Trial</span>
                    : fmtCurrency(item.price_cents, item.currency)
                  }
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Settle form — operator only, only when pending_payment */}
        {order.status === 'pending_payment' && (
          <div style={{
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
          }}>
            <p style={{
              fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
              fontWeight: 600, marginBottom: 'var(--sp-3)',
            }}>Record settlement</p>
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)', marginBottom: 'var(--sp-3)' }}>
              Payment integration is pending (ADR-025). Record manual settlement below.
            </p>
            <input
              type="text"
              placeholder="Settlement reference (PO number, transfer ref…)"
              value={settleRef}
              onChange={(e) => setSettleRef(e.target.value)}
              style={{
                width: '100%', padding: 'var(--sp-2) var(--sp-3)',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
                border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface)', color: 'var(--fg-1)',
                marginBottom: 'var(--sp-3)', boxSizing: 'border-box',
              }}
            />
            {settleError && (
              <p style={{ color: 'var(--dp-crimson-600)', fontSize: 'var(--fs-meta)', marginBottom: 'var(--sp-2)' }}>
                {settleError}
              </p>
            )}
            <button
              className="dp-btn dp-btn--primary"
              onClick={handleSettle}
              disabled={settling}
            >
              {settling ? 'Recording…' : 'Mark as settled'}
            </button>
          </div>
        )}

        {order.status === 'settled' && (
          <div style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            <strong>Settled</strong> {order.settled_at ? fmtDate(order.settled_at) : ''}
            {order.settled_by ? ` by ${order.settled_by}` : ''}
            {order.settlement_ref ? ` · ref: ${order.settlement_ref}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderHistoryView({ token }: { token: string | null }) {
  const { state, reload } = useOrders(token);
  const [selectedOrder, setSelectedOrder] = React.useState<OrderRow | null>(null);

  const orders: OrderRow[] = state.tag === 'loaded' ? state.orders : [];

  return (
    <div className="dp-shell__content">
      <div style={{ marginBottom: 'var(--sp-6)', display: 'flex', alignItems: 'baseline', gap: 'var(--sp-4)' }}>
        <div>
          <p className="dp-eyebrow">Orders</p>
          <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Order history</h2>
          <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            All checkout orders. Settlement is manual until ADR-025 billing rails are live.
          </p>
        </div>
        <button
          onClick={reload}
          style={{
            marginLeft: 'auto', padding: 'var(--sp-2) var(--sp-4)',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
            background: 'var(--bg-recessed)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--fg-2)',
            flexShrink: 0,
          }}
        >Refresh</button>
      </div>

      {state.tag === 'loading' && (
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>Loading…</p>
      )}
      {state.tag === 'error' && (
        <p style={{ color: 'var(--dp-crimson-600)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
          Failed to load orders ({state.errorCode}).
        </p>
      )}
      {state.tag === 'loaded' && orders.length === 0 && (
        <div style={{
          padding: 'var(--sp-12)', textAlign: 'center', color: 'var(--fg-3)',
          fontFamily: 'var(--font-sans)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
        }}>
          No orders yet. Use the cart to place your first order.
        </div>
      )}

      {orders.length > 0 && (
        <div style={{
          overflowX: 'auto', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--bg-recessed)' }}>
                {['Tenant', 'Rail', 'Total', 'Items', 'Status', 'Created', ''].map((h) => (
                  <th key={h} style={{
                    padding: 'var(--sp-3) var(--sp-4)', textAlign: 'left', fontWeight: 600,
                    fontSize: 'var(--fs-meta)', color: 'var(--fg-3)',
                    textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)', fontWeight: 500 }}>
                    {order.tenant_name ?? <span style={{ color: 'var(--fg-4)' }}>{order.tenant_id?.slice(0,8) ?? '—'}</span>}
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                    <RailBadge rail={order.rail} />
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)' }}>
                    {fmtCurrency(order.total_cents, order.currency)}
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)', color: 'var(--fg-3)', textAlign: 'center' }}>
                    {order.item_count}
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                    <StatusBadge status={order.status} />
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)', fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-meta)', color: 'var(--fg-3)', whiteSpace: 'nowrap' }}>
                    {fmtDate(order.created_at)}
                  </td>
                  <td style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="dp-btn dp-btn--sm dp-btn--ghost"
                    >
                      {order.status === 'pending_payment' ? 'Settle' : 'Detail'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          token={token}
          onClose={() => setSelectedOrder(null)}
          onSettled={() => { setSelectedOrder(null); reload(); }}
        />
      )}
    </div>
  );
}


// ── Suspense fallback for the code-split operator chunk ───────────────────────

function AdminLoadingView() {
  return (
    <div
      className="dp-shell__content"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--sp-12)', color: 'var(--fg-3)',
        fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
      }}
    >
      Loading…
    </div>
  );
}

// ── Placeholder view ──────────────────────────────────────────────────────────

function AdminPlaceholderView({ section }: { section: string }) {
  return (
    <div
      className="dp-shell__content"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-12)',
        color: 'var(--fg-3)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <p className="dp-eyebrow">{section}</p>
      <p style={{ fontSize: 'var(--fs-body-sm)' }}>
        This section is a placeholder — implementation planned for cycle 3+.
      </p>
    </div>
  );
}

// ── AdminPage ─────────────────────────────────────────────────────────────────

export default function AdminPage({ activePath, onNavigate }: AdminPageProps) {
  const config = useConfig();
  const { hasRole, token } = useAuth();

  // Role gate on the authenticated broker modes — defense in depth ON TOP of the
  // mode gate, never instead of it (decision-2026-07-25-broker-mode-split).
  const isAuthedBroker = isAuthenticatedBroker(config.instance.mode);
  // Role set moved to auth/surfaceAccess 2026-08-02 so NavRail offers this
  // destination to exactly the identities this page admits. Same roles as before.
  const canAccess = !isAuthedBroker || canUseAdminSurface(hasRole);

  // Does this deployment have an operator provisioning surface AT ALL? Meta only.
  const operatorSurfaceRegistered = hasOperatorProvisioning(config.instance.mode);

  // Parse sub-client detail route: /admin/sub-clients/:slug
  const isSubClientDetail = activePath.startsWith('/admin/sub-clients/');
  const subClientSlug = isSubClientDetail
    ? activePath.replace('/admin/sub-clients/', '')
    : null;

  return (
    <>
      {!canAccess ? (
        <AdminAccessDenied />
      ) : isSubClientDetail && subClientSlug ? (
        <SubClientDetailPage
          slug={subClientSlug}
          onBack={() => onNavigate('/admin/sub-clients')}
        />
      ) : activePath === '/admin/sub-clients' ? (
        <SubClientsView onNavigate={onNavigate} />
      ) : activePath === '/admin/subscriptions' ? (
        <SubscriptionQueueView token={token} />
      ) : activePath === '/admin/orders' ? (
        <OrderHistoryView token={token} />
      ) : activePath === '/admin/audit-log' ? (
        <AuditLogView />
      ) : activePath === '/admin/sandbox-provisioning' ? (
        operatorSurfaceRegistered ? (
          <Suspense fallback={<AdminLoadingView />}>
            <SandboxProvisioningPage />
          </Suspense>
        ) : (
          // Unreachable in practice — AppShell resolves the route to NOT FOUND
          // before AdminPage renders. Kept so the mode gate holds even if this
          // component is mounted directly.
          <RouteNotFound route={activePath} onHome={() => onNavigate('/')} />
        )
      ) : (
        <AdminPlaceholderView section="Tenants" />
      )}


    </>
  );
}
